import React from 'react';
import { useTranslation } from 'react-i18next';

import { CreateProject } from '@/console/features/projects/components/create-project/create-project';
import { AddProject } from '@/console/features/projects/components/project-drawer/add-project';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { MultiDrawer } from '@/shared/components/ui/multi-drawer/multi-drawer';
import {
  TCustomMultiDrawerProps,
  TMultiDrawerTab,
} from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

export const ProjectDrawer: React.FC<TCustomMultiDrawerProps> = ({
  handlePopoverClose,
  assetId,
  component = 'MenuItem',
  icon = <ProjectsIcon />,
  disableTabs = false,
}) => {
  const { t } = useTranslation();
  const { can } = usePermissionCheck();
  const hasUpdatePermission = can('project:updateProject', assetId);
  const hasCreatePermission = can('project:createProject', assetId);

  const addProjectTab: TMultiDrawerTab = {
    title: t('projects:select_existing_projects'),
    disabled: !assetId,
    content: (handleClose) => (
      <AddProject groupId={assetId!} handlePopoverClose={handleClose} />
    ),
  };

  const createProjectTab: TMultiDrawerTab = {
    title: t('projects:create_project'),
    content: (handleClose) => (
      <CreateProject groupId={assetId!} handleClose={handleClose} />
    ),
  };

  const getTabs = () => {
    const tabs: TMultiDrawerTab[] = [];
    if (hasUpdatePermission && assetId) {
      tabs.push(addProjectTab);
    }
    if (hasCreatePermission) {
      tabs.push(createProjectTab);
    }
    return tabs;
  };

  const tabs = getTabs();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <MultiDrawer
      title={t('projects:add_project')}
      tabs={tabs}
      componentLabel={t('projects:add_project')}
      defaultTab={0}
      component={component}
      icon={icon}
      width="550px"
      disableTabs={disableTabs || tabs.length === 1}
      handlePopoverClose={handlePopoverClose}
      tabsFullWidth
    />
  );
};
